import type { MDXComponents } from 'mdx/types';
import { MDXProvider } from '@mdx-js/react';
import { Link } from 'react-router-dom';
import { ScadBlock } from './ScadBlock';
import type { ScadBlockProps } from './ScadBlock';
import { AiExplainer } from './AiExplainer';
import { Quiz } from './Quiz';
import { PostCard } from './PostCard';
import { Tip } from './Tip';
import { FunctionSignature } from './FunctionSignature';
import { parseMeshpitHref } from '@/utils/parameterUtils';
import { ActionBadge } from '@/components/parameter/MarkdownBlock';

/**
 * Maps HTML elements and custom components used inside MDX files.
 *
 * Standard prose elements (h1-h3, p, a, code, etc.) get consistent
 * styling via Tailwind's `prose` class in BlogLayout, so most overrides
 * here are for behaviour (e.g. routing links) or for custom components
 * that need to be available without an explicit import in every MDX file.
 */

// ---------------------------------------------------------------------------
// Backward-compat shims
// ---------------------------------------------------------------------------
// ScadPreview and ScadPlayground are mapped to ScadBlock so all existing MDX
// posts continue to work without changes.

interface ScadPreviewCompatProps extends Omit<ScadBlockProps, 'variant'> {
  showcaseMode?: boolean;
}

function ScadPreview({ showcaseMode, ...rest }: ScadPreviewCompatProps) {
  return <ScadBlock variant={showcaseMode ? 'showcase' : 'viewer'} {...rest} />;
}

function ScadPlayground(props: Omit<ScadBlockProps, 'variant'>) {
  return <ScadBlock variant="editor" {...props} />;
}

// ---------------------------------------------------------------------------
// Component map
// ---------------------------------------------------------------------------

const components: MDXComponents = {
  // Route internal links through React Router; open external links in a new tab.
  // meshpit: action links are rendered as inert styled badges — the same visual
  // as in the parameter panel, but without callbacks since there is no live
  // parameter panel in a blog post context.
  a: ({ href = '', children, ...props }) => {
    if (href.startsWith('meshpit:')) {
      const action = parseMeshpitHref(href);
      if (action.type !== 'unknown') {
        return <ActionBadge>{children}</ActionBadge>;
      }
    }

    const isInternal =
      href.startsWith('/') || href.startsWith('#') || href === '';
    if (isInternal) {
      return (
        <Link to={href} {...props}>
          {children}
        </Link>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  },

  // Custom components available in every MDX file without an import
  ScadBlock,
  // Backward-compat aliases — existing posts using ScadPreview/ScadPlayground
  // continue to work unchanged.
  ScadPreview,
  ScadPlayground,
  AiExplainer,
  Quiz,
  PostCard,
  Tip,
  FunctionSignature,
};

export function BlogMDXProvider({ children }: { children: React.ReactNode }) {
  return <MDXProvider components={components}>{children}</MDXProvider>;
}
